import { useEffect, useState } from 'react';
import { useCollectBadgeMutation } from '../slices/badgeApi';
import { isBadgeReadyForAutoCollection } from '../utils/credentialingUtils';

// Shared registry prevents duplicate auto-collect attempts across components
const autoCollectRegistry = new Map();

/**
 * Automatically collect badges that are ready without additional user action.
 * Returns a status map keyed by badge id: 'pending' | 'success' | 'error'.
 */
const useAutoCollectBadges = (badges = []) => {
  const [collectBadge] = useCollectBadgeMutation();
  const [statuses, setStatuses] = useState({});

  useEffect(() => {
    if (!Array.isArray(badges) || badges.length === 0) {
      return;
    }

    badges.forEach((badgeEntry) => {
      const badgeId = badgeEntry?.badge?.id;
      if (!badgeId) {
        return;
      }

      const registryState = autoCollectRegistry.get(badgeId);
      if (registryState) {
        setStatuses((prev) => {
          if (prev[badgeId] === registryState.status) {
            return prev;
          }
          return { ...prev, [badgeId]: registryState.status };
        });
      }

      if (!isBadgeReadyForAutoCollection(badgeEntry)) {
        return;
      }

      // Avoid re-issuing the same request while an attempt is active or succeeded
      if (registryState?.status === 'pending' || registryState?.status === 'success') {
        return;
      }

      autoCollectRegistry.set(badgeId, { status: 'pending' });
      setStatuses((prev) => ({ ...prev, [badgeId]: 'pending' }));

      collectBadge({ badgeId })
        .unwrap()
        .then(() => {
          autoCollectRegistry.set(badgeId, { status: 'success' });
          setStatuses((prev) => ({ ...prev, [badgeId]: 'success' }));
        })
        .catch(() => {
          autoCollectRegistry.set(badgeId, { status: 'error' });
          setStatuses((prev) => ({ ...prev, [badgeId]: 'error' }));
        });
    });
  }, [badges, collectBadge]);

  return statuses;
};

export default useAutoCollectBadges;
